/**
 * Version Check UI Components
 * Handles the visual presentation of update notifications
 */

// Constants for UI timing
export const UPDATE_COUNTDOWN_SECONDS = 6;
export const COUNTDOWN_INTERVAL_MS = 1000;

/**
 * Creates and displays a blocking overlay modal for update notifications
 * @param initialSeconds - Initial countdown value in seconds
 * @param onCancel - Callback when user clicks "Later"
 * @param onReload - Callback when user clicks "Update Now"
 */
export function createBlockingOverlay(
  initialSeconds: number,
  onCancel: () => void,
  onReload: () => void
): void {
  // Inject styles and animations
  const styleId = "app-update-styles";
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement("style");
    styleSheet.id = styleId;
    styleSheet.textContent = `
      @keyframes spin { 
        to { transform: rotate(360deg); } 
      }
      @keyframes fadeIn { 
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp { 
        from { 
          opacity: 0; 
          transform: translateY(20px); 
        }
        to { 
          opacity: 1; 
          transform: translateY(0); 
        }
      }
      @keyframes pulse {
        0%, 100% { 
          transform: scale(1);
          opacity: 1;
        }
        50% { 
          transform: scale(1.05);
          opacity: 0.9;
        }
      }
    `;
    document.head.appendChild(styleSheet);
  }

  // Overlay backdrop
  const overlay = document.createElement("div");
  overlay.id = "app-update-overlay";
  Object.assign(overlay.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(15, 23, 42, 0.75)",
    zIndex: "99999",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    backdropFilter: "blur(8px)",
    animation: "fadeIn 0.3s ease-out",
  });

  // Modal card container
  const card = document.createElement("div");
  Object.assign(card.style, {
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    borderRadius: "20px",
    padding: "3rem 2.5rem",
    maxWidth: "460px",
    width: "90%",
    boxShadow:
      "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    textAlign: "center",
    position: "relative",
    animation: "slideUp 0.4s ease-out",
  });

  // Top accent bar
  const accentBar = document.createElement("div");
  Object.assign(accentBar.style, {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    height: "4px",
    background: "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
    borderRadius: "20px 20px 0 0",
  });

  // Icon/Badge with countdown
  const badge = document.createElement("div");
  badge.id = "app-update-badge";
  badge.innerText = String(initialSeconds);
  Object.assign(badge.style, {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
    color: "#ffffff",
    fontSize: "1.75rem",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.5rem",
    boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.4)",
    animation: "pulse 2s ease-in-out infinite",
  });

  // Title
  const title = document.createElement("h1");
  title.innerText = "🎉 New Version Available";
  Object.assign(title.style, {
    fontSize: "1.75rem",
    marginBottom: "0.75rem",
    fontWeight: "700",
    color: "#0f172a",
    letterSpacing: "-0.025em",
    lineHeight: "1.2",
  });

  // Subtitle
  const subtitle = document.createElement("p");
  subtitle.innerText = "We've made improvements to enhance your experience";
  Object.assign(subtitle.style, {
    fontSize: "0.95rem",
    color: "#64748b",
    marginBottom: "1.5rem",
    lineHeight: "1.5",
  });

  // Message with countdown
  const message = document.createElement("p");
  message.id = "app-update-message";
  const plural = initialSeconds !== 1 ? "s" : "";
  message.innerText = `Auto-updating in ${initialSeconds} second${plural}`;
  Object.assign(message.style, {
    fontSize: "0.875rem",
    color: "#475569",
    marginBottom: "2rem",
    fontWeight: "500",
    padding: "0.75rem 1rem",
    backgroundColor: "#f1f5f9",
    borderRadius: "8px",
    display: "inline-block",
  });

  // Action Buttons Container
  const buttonContainer = document.createElement("div");
  Object.assign(buttonContainer.style, {
    display: "flex",
    gap: "0.75rem",
    marginTop: "1.5rem",
    justifyContent: "center",
  });

  // Update Now Button (Primary)
  const reloadBtn = document.createElement("button");
  reloadBtn.innerText = "Update Now";
  Object.assign(reloadBtn.style, {
    padding: "0.875rem 2rem",
    fontSize: "0.95rem",
    fontWeight: "600",
    color: "#ffffff",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px -2px rgba(59, 130, 246, 0.4)",
    flex: "1",
  });
  reloadBtn.onmouseover = () => {
    reloadBtn.style.transform = "translateY(-2px)";
    reloadBtn.style.boxShadow = "0 8px 16px -4px rgba(59, 130, 246, 0.5)";
  };
  reloadBtn.onmouseout = () => {
    reloadBtn.style.transform = "translateY(0)";
    reloadBtn.style.boxShadow = "0 4px 12px -2px rgba(59, 130, 246, 0.4)";
  };
  reloadBtn.onclick = onReload;

  // Cancel Button (Secondary)
  const cancelBtn = document.createElement("button");
  cancelBtn.innerText = "Later";
  Object.assign(cancelBtn.style, {
    padding: "0.875rem 1.5rem",
    fontSize: "0.95rem",
    fontWeight: "600",
    color: "#475569",
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  });
  cancelBtn.onmouseover = () => {
    cancelBtn.style.backgroundColor = "#f8fafc";
    cancelBtn.style.borderColor = "#cbd5e1";
    cancelBtn.style.transform = "translateY(-2px)";
  };
  cancelBtn.onmouseout = () => {
    cancelBtn.style.backgroundColor = "#ffffff";
    cancelBtn.style.borderColor = "#e2e8f0";
    cancelBtn.style.transform = "translateY(0)";
  };
  cancelBtn.onclick = onCancel;

  buttonContainer.append(reloadBtn, cancelBtn);
  card.append(accentBar, badge, title, subtitle, message, buttonContainer);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

/**
 * Updates the countdown display in the overlay
 * @param seconds - Remaining seconds
 */
export function updateOverlayCountdown(seconds: number): void {
  // Update badge number
  const badge = document.getElementById("app-update-badge");
  if (badge) {
    badge.innerText = String(seconds);
  }

  // Update message text
  const message = document.getElementById("app-update-message");
  if (message) {
    const plural = seconds !== 1 ? "s" : "";
    message.innerText = `Auto-updating in ${seconds} second${plural}`;
  }
}

/**
 * Removes the update overlay from the DOM
 */
export function removeUpdateOverlay(): void {
  const overlay = document.getElementById("app-update-overlay");
  if (overlay) {
    overlay.remove();
  }
}
